import React, { useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, Calendar, Lock, Mail, User, Users, Palette, CheckCircle, AlertCircle, Instagram, Facebook, Twitter, Linkedin, X as CloseIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ArtCategory } from '../types';
import { authService } from '../services/authService';
import ParticlesBackground from './ParticlesBackground';
import ErrorBoundary from './ErrorBoundary';
import { useLanguage } from '../context/LanguageContext';

const AuthForm: React.FC = () => {
  const { t, language, select } = useLanguage();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStep, setResetStep] = useState<'email' | 'otp'>('email');
  const [otpCode, setOtpCode] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    fullName: '',
    birthDate: '',
    userType: 'visitor' as 'artist' | 'visitor',
    artCategories: [] as ArtCategory[],
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [acceptTerms, setAcceptTerms] = useState(false);

  const { login, register } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [diditStatus, setDiditStatus] = useState<'idle' | 'approved' | 'declined' | 'in_review'>('idle');
  const [diditSessionId, setDiditSessionId] = useState<string | null>(null);
  const [showDiditModal, setShowDiditModal] = useState(false);

  const diditVerificationUrl =
    (typeof import.meta !== 'undefined' &&
      import.meta.env &&
      import.meta.env.VITE_DIDIT_VERIFICATION_URL) ||
    'https://verify.didit.me/verify/yAh1p8b6zhVa5TaQRcil2A';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const statusParam = params.get('status');
    const sessionId = params.get('verificationSessionId');

    if (statusParam) {
      const normalized = statusParam.trim().toLowerCase();
      if (normalized === 'approved') {
        setDiditStatus('approved');
      } else if (normalized === 'declined') {
        setDiditStatus('declined');
      } else if (normalized === 'in review' || normalized === 'in_review') {
        setDiditStatus('in_review');
      }
    }

    if (sessionId) {
      setDiditSessionId(sessionId);
    }
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Log para depuración
      if (event.data && event.data.type === 'didit-result') {
        console.log('AuthForm recibió mensaje de Didit:', event.data);
      }

      const data = event.data as { type?: string; status?: string; verificationSessionId?: string } | null;
      if (!data || data.type !== 'didit-result') return;

      const status = (data.status || '').trim().toLowerCase();
      console.log('Procesando estado Didit:', status);
      if (status === 'approved') {
        setDiditStatus('approved');
      } else if (status === 'declined') {
        setDiditStatus('declined');
      } else if (status === 'in review' || status === 'in_review') {
        setDiditStatus('in_review');
      }

      if (data.verificationSessionId) {
        setDiditSessionId(data.verificationSessionId);
      }

      setShowDiditModal(false);
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const categoryLabels = useMemo<Record<ArtCategory, string>>(() => ({
    music: t('categories.music', 'Música'),
    writing: t('categories.writing', 'Escritura'),
    tattoo: t('categories.tattoo', 'Tatuajes'),
    digital_design: t('categories.digital_design', 'Diseño Digital'),
    fashion_design: t('categories.fashion_design', 'Diseño de Moda'),
    photography: t('categories.photography', 'Fotografía'),
    sculpture: t('categories.sculpture', 'Escultura'),
    painting: t('categories.painting', 'Pintura'),
    dance: t('categories.dance', 'Danza'),
    filmmaking: t('categories.filmmaking', 'Cine'),
    makeup: t('categories.makeup', 'Maquillaje'),
  }), [t]);

  const validateField = (field: string, value: string) => {
    let errorMessage = '';

    switch (field) {
      case 'email':
        if (!value) {
          errorMessage = t('auth.validation.emailRequired', 'El email es obligatorio');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errorMessage = t('auth.validation.emailInvalid', 'Email inválido');
        }
        break;
      case 'password':
        if (!value) {
          errorMessage = t('auth.validation.passwordRequired', 'La contraseña es obligatoria');
        } else if (value.length < 6) {
          errorMessage = t('auth.validation.passwordLength', 'Mínimo 6 caracteres');
        }
        break;
      case 'username':
        if (!isLogin && !value) {
          errorMessage = t('auth.validation.usernameRequired', 'El usuario es obligatorio');
        } else if (!isLogin && value && value.length < 3) {
          errorMessage = t('auth.validation.usernameLength', 'Mínimo 3 caracteres');
        }
        break;
      case 'fullName':
        if (!isLogin && !value) {
          errorMessage = t('auth.validation.fullNameRequired', 'El nombre es obligatorio');
        }
        break;
      case 'birthDate':
        if (!isLogin && !value) {
          errorMessage = t('auth.validation.birthDateRequired', 'La fecha de nacimiento es obligatoria');
        } else if (!isLogin && value) {
          const birthDate = new Date(value);
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const m = today.getMonth() - birthDate.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          if (age < 18) {
            errorMessage = t('auth.validation.ageRequirement', 'Debes ser mayor de 18 años para registrarte.');
          }
        }
        break;
    }

    setValidationErrors(prev => {
      const newErrors = { ...prev };
      if (errorMessage) {
        newErrors[field] = errorMessage;
      } else {
        delete newErrors[field];
      }
      return newErrors;
    });
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    // Validate email
    if (!formData.email) {
      errors.email = t('auth.validation.emailRequired', 'El email es obligatorio');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t('auth.validation.emailInvalid', 'Email inválido');
    }

    if (!formData.password) {
      errors.password = t('auth.validation.passwordRequired', 'La contraseña es obligatoria');
    } else if (formData.password.length < 6) {
      errors.password = t('auth.validation.passwordLength', 'Mínimo 6 caracteres');
    }

    if (!acceptTerms) {
      errors.terms = t(
        'auth.validation.termsRequired',
        'Debes aceptar los términos y condiciones para continuar.'
      );
    }

    if (!isLogin) {
      if (!formData.username) {
        errors.username = t('auth.validation.usernameRequired', 'El usuario es obligatorio');
      } else if (formData.username.length < 3) {
        errors.username = t('auth.validation.usernameLength', 'Mínimo 3 caracteres');
      }

      if (!formData.fullName) {
        errors.fullName = t('auth.validation.fullNameRequired', 'El nombre es obligatorio');
      }

      if (!formData.birthDate) {
        errors.birthDate = t('auth.validation.birthDateRequired', 'La fecha de nacimiento es obligatoria');
      } else {
        const birthDate = new Date(formData.birthDate);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        if (age < 18) {
          errors.birthDate = t('auth.validation.ageRequirement', 'Debes ser mayor de 18 años para registrarte.');
        }
      }

    }

    return errors;
  };

  const handleSocialAuth = async (provider: 'google' | 'apple') => {
    setError('');
    setSubmitting(true);
    setLoading(true);

    try {
      if (provider === 'google') {
        await authService.signInWithGoogle();
      } else if (provider === 'apple') {
        await authService.signInWithApple();
      }
      // The auth state change listener will handle the rest
    } catch (err) {
      const providerName = provider === 'google' ? 'Google' : 'Apple';
      if (err instanceof Error) {
        if (err.message.includes('popup')) {
          setError(t('auth.errors.popupBlocked', 'Por favor permite las ventanas emergentes para continuar con la autenticación'));
        } else if (err.message.includes('cancelled')) {
          setError(t('auth.errors.userCancelled', 'Autenticación cancelada por el usuario'));
        } else {
          setError(`${t('auth.errors.socialAuthPrefix', 'Error de autenticación con')} ${providerName}: ${err.message}`);
        }
      } else {
        setError(`${t('auth.errors.socialAuthPrefix', 'Error de autenticación con')} ${providerName}`);
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleStartDiditVerification = () => {
    if (!diditVerificationUrl) {
      setError(
        t(
          'auth.didit.misconfigured',
          'La verificación de identidad no está disponible en este momento. Contacta con soporte.'
        )
      );
      return;
    }
    setShowDiditModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent double submission
    if (submitting || loading) {
      return;
    }

    setError('');
    setSubmitting(true);
    setLoading(true);

    // Validate form synchronously
    const errors = validateForm();
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      setLoading(false);
      setSubmitting(false);
      return;
    }

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        await register({
          ...formData,
          diditSessionId,
          diditStatus
        });
      }
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('User already registered') || err.message.includes('user_already_exists')) {
          setError(t('auth.errors.emailExists', 'Este email ya está registrado. Por favor, inicia sesión o usa otro email.'));
        } else {
          setError(err.message);
        }
      } else {
        setError(t('auth.errors.generic', 'Error de autenticación. Por favor, verifica tus credenciales.'));
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      if (resetStep === 'email') {
        await authService.sendRecoveryOtp(resetEmail);
        setMessage(t('auth.reset.otpSent', 'Se ha enviado un código de seguridad a tu email'));
        setResetStep('otp');
        setResendTimer(60);
      } else {
        await authService.verifyRecoveryOtp(resetEmail, otpCode);
        // Redirect to reset password page with token handling handled by Supabase session
        window.location.href = '/reset-password';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.reset.error', 'Error al procesar la solicitud'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setError('');
    setMessage('');
    setLoading(true);
    try {
      await authService.sendRecoveryOtp(resetEmail);
      setMessage(select({ es: 'Código reenviado exitosamente', en: 'Code resent successfully' }));
      setResendTimer(60);
    } catch {
      setError(select({ es: 'Error al reenviar el código', en: 'Error resending code' }));
    } finally {
      setLoading(false);
    }
  };

  const toggleArtCategory = (category: ArtCategory) => {
    setFormData(prev => ({
      ...prev,
      artCategories: prev.artCategories.includes(category)
        ? prev.artCategories.filter(c => c !== category)
        : [...prev.artCategories, category]
    }));
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        {/* Modern gradient background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900"></div>
          <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/20 via-purple-900/40 to-pink-900/20"></div>
        </div>

        <ParticlesBackground />

        <div className="relative z-10 w-full max-w-md mx-auto p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 transform transition-all">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Lock className="text-white" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.reset.title', 'Recuperar Contraseña')}</h2>
              <p className="text-gray-600">{t('auth.reset.subtitle', 'Te enviaremos un enlace para restablecer tu contraseña')}</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="text-red-500 mr-3" size={20} />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              </div>
            )}

            {message && (
              <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
                <div className="flex items-center">
                  <CheckCircle className="text-green-500 mr-3" size={20} />
                  <p className="text-green-700 text-sm">{message}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleForgotPassword} className="space-y-6">
              {resetStep === 'email' ? (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    {t('auth.emailLabel', 'Dirección de email')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="email"
                      required
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder={t('auth.emailPlaceholder', 'tu@email.com')}
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        {select({ es: 'Código de seguridad (OTP)', en: 'Security Code (OTP)' })}
                      </label>
                      <button
                        type="button"
                        onClick={() => setResetStep('email')}
                        className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                      >
                        {select({ es: 'Cambiar email', en: 'Change email' })}
                      </button>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        required
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all tracking-[0.5em] font-mono text-center text-xl font-bold"
                        placeholder="000000"
                        maxLength={6}
                        autoComplete="one-time-code"
                      />
                    </div>
                    <p className="text-xs text-gray-500 text-center mt-2">
                      {select({ es: 'Introduce el código de 6 dígitos que enviamos a', en: 'Enter the 6-digit code we sent to' })} <span className="font-medium text-gray-700">{resetEmail}</span>
                    </p>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">
                      {resendTimer > 0 
                        ? select({ es: `Reenviar en ${resendTimer}s`, en: `Resend in ${resendTimer}s` })
                        : select({ es: '¿No recibiste el código?', en: 'Did not receive the code?' })
                      }
                    </span>
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={resendTimer > 0 || loading}
                      className={`font-medium transition-colors ${
                        resendTimer > 0 || loading
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-purple-600 hover:text-purple-700'
                      }`}
                    >
                      {select({ es: 'Reenviar código', en: 'Resend code' })}
                    </button>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    <span>{t('auth.loading', 'Procesando...')}</span>
                  </div>
                ) : (
                  <span>
                    {resetStep === 'email' 
                      ? t('auth.reset.sendButton', 'Enviar código') 
                      : select({ es: 'Verificar código', en: 'Verify code' })
                    }
                  </span>
                )}
              </button>
            </form>

            <div className="mt-8 text-center">
              <button
                onClick={() => setShowForgotPassword(false)}
                className="text-purple-600 hover:text-purple-700 font-semibold transition-colors duration-200"
              >
                {t('auth.reset.backToLogin', '← Volver al inicio de sesión')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Professional gradient background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/20 via-purple-900/40 to-pink-900/20"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-900/20 via-slate-900/40 to-slate-900"></div>
      </div>

      <ParticlesBackground />

      {/* Floating elements for visual appeal */}
      <div className="absolute top-20 left-20 w-32 h-32 bg-white/5 rounded-full blur-xl animate-pulse"></div>
      <div className="absolute bottom-32 right-20 w-40 h-40 bg-purple-500/10 rounded-full blur-xl animate-pulse delay-1000"></div>
      <div className="absolute top-1/2 left-10 w-24 h-24 bg-pink-500/5 rounded-full blur-xl animate-pulse delay-500"></div>

      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-6xl flex items-center justify-center">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 w-full max-w-5xl">

            {/* Left Side - Branding */}
            <div className="hidden lg:flex flex-col justify-center items-center text-center">
              <div className="relative">
                <img
                  src="https://soartistic.com/wp-content/uploads/2025/06/logo-blanco-sin-fondo.svg"
                  alt="SoArtistic"
                  className="h-20 mx-auto mb-8 brightness-0 invert drop-shadow-2xl"
                />

                <h1 className="text-5xl font-bold bg-gradient-to-r from-white via-purple-200 to-pink-200 bg-clip-text text-transparent mb-6">
                  SoArtistic
                </h1>

                <p className="text-xl text-white/90 mb-8 leading-relaxed max-w-md">
                  {t('auth.hero.tagline', 'La plataforma definitiva donde el arte cobra vida y los artistas se conectan con el mundo')}
                </p>

                <div className="grid grid-cols-1 gap-4 max-w-sm text-left">
                  <div className="flex items-center text-white/80">
                    <div className="w-2 h-2 bg-purple-400 rounded-full mr-4"></div>
                    <span className="text-sm">{t('auth.hero.benefit1', 'Conecta con artistas globales')}</span>
                  </div>
                  <div className="flex items-center text-white/80">
                    <div className="w-2 h-2 bg-pink-400 rounded-full mr-4"></div>
                    <span className="text-sm">{t('auth.hero.benefit2', 'Vende y compra arte original')}</span>
                  </div>
                  <div className="flex items-center text-white/80">
                    <div className="w-2 h-2 bg-blue-400 rounded-full mr-4"></div>
                    <span className="text-sm">{t('auth.hero.benefit3', 'Colabora en proyectos únicos')}</span>
                  </div>
                  <div className="flex items-center text-white/80">
                    <div className="w-2 h-2 bg-green-400 rounded-full mr-4"></div>
                    <span className="text-sm">{t('auth.hero.benefit4', 'Exhibe tu talento al mundo')}</span>
                  </div>
                </div>

                {/* Social Media Links */}
                <div className="mt-12 flex items-center gap-4">
                  <ErrorBoundary>
                    <a
                      href="https://www.instagram.com/soartisticarte/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all transform hover:scale-110"
                      aria-label="Instagram"
                    >
                      <Instagram size={20} />
                    </a>
                    <a
                      href="https://www.facebook.com/people/Soartistic-Soartistic/pfbid073MyHSXBTRaERzH66nBvXXfmS22rxkKWkiaxY9aAeoEGEVAzp84zbmE2TUfB2kARl/?sk=about"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all transform hover:scale-110"
                      aria-label="Facebook"
                    >
                      <Facebook size={20} />
                    </a>
                    <a
                      href="https://x.com/SoartisticArt"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all transform hover:scale-110"
                      aria-label="X (Twitter)"
                    >
                      <Twitter size={20} />
                    </a>
                    <a
                      href="https://www.linkedin.com/company/107366234/admin/page-posts/published/?shareMsgArgs=null"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all transform hover:scale-110"
                      aria-label="LinkedIn"
                    >
                      <Linkedin size={20} />
                    </a>
                  </ErrorBoundary>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="flex items-center justify-center">
              <div className="w-full max-w-md">
                <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 transform transition-all">

                  {/* Mobile Logo */}
                  <div className="lg:hidden text-center mb-8">
                    <img
                      src="https://soartistic.com/wp-content/uploads/2025/06/logo-blanco-sin-fondo.svg"
                      alt="SoArtistic"
                      className="h-12 mx-auto mb-4 brightness-0"
                    />
                  </div>

                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
                      {isLogin ? t('auth.loginTitle', 'Bienvenido') : t('auth.registerTitle', 'Crear Cuenta')}
                    </h2>
                    <p className="text-gray-600">
                      {isLogin ? t('auth.loginSubtitle', 'Accede a tu cuenta de artista') : t('auth.registerSubtitle', 'Únete a la comunidad creativa')}
                    </p>
                  </div>

                  {error && (
                    <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                      <div className="flex items-center">
                        <AlertCircle className="text-red-500 mr-3" size={20} />
                        <p className="text-red-700 text-sm">{error}</p>
                      </div>
                    </div>
                  )}

                  {message && (
                    <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
                      <div className="flex items-center">
                        <CheckCircle className="text-green-500 mr-3" size={20} />
                        <p className="text-green-700 text-sm">{message}</p>
                      </div>
                    </div>
                  )}

                  {/* Social Auth for Login */}
                  {isLogin && (
                    <div className="space-y-3 mb-8">
                      <button
                        type="button"
                        onClick={() => handleSocialAuth('google')}
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-3 px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-blue-400 transition-all duration-200 disabled:opacity-50 group"
                      >
                        <div className="w-5 h-5 bg-white rounded-sm flex items-center justify-center shadow-sm">
                          <svg viewBox="0 0 24 24" className="w-4 h-4">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                          </svg>
                        </div>
                        <span className="font-medium text-gray-700 group-hover:text-blue-700 transition-colors">{t('auth.social.google', 'Continuar con Google')}</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleSocialAuth('apple')}
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-3 px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-800 transition-all duration-200 disabled:opacity-50 group"
                      >
                        <div className="w-5 h-5 bg-black rounded-sm flex items-center justify-center">
                          <svg viewBox="0 0 24 24" className="w-4 h-4 text-white">
                            <path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                          </svg>
                        </div>
                        <span className="font-medium text-gray-700 group-hover:text-gray-900 transition-colors">{t('auth.social.apple', 'Continuar con Apple')}</span>
                      </button>

                      <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-300"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-4 bg-white text-gray-500 font-medium">{t('auth.social.orEmail', 'o continúa con email')}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {!isLogin && (
                      <>
                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">
                            {t('auth.fullNameLabel', 'Nombre completo')}
                          </label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                              type="text"
                              required
                              value={formData.fullName}
                              onChange={(e) => {
                                setFormData({ ...formData, fullName: e.target.value });
                                validateField('fullName', e.target.value);
                              }}
                              className={`w-full pl-11 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${validationErrors.fullName ? 'border-red-500' : 'border-gray-300'
                                }`}
                              placeholder={t('auth.fullNamePlaceholder', 'Tu nombre completo')}
                            />
                          </div>
                          {validationErrors.fullName && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.fullName}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">
                            {t('auth.usernameLabel', 'Nombre de usuario')}
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">@</span>
                            <input
                              type="text"
                              required
                              value={formData.username}
                              onChange={(e) => {
                                let value = e.target.value;
                                // Remove @ from the beginning if user types it
                                if (value.startsWith('@')) {
                                  value = value.substring(1);
                                }
                                setFormData({ ...formData, username: value });
                                validateField('username', e.target.value);
                              }}
                              className={`w-full pl-8 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${validationErrors.username ? 'border-red-500' : 'border-gray-300'
                                }`}
                              placeholder={t('auth.usernamePlaceholder', 'usuario')}
                              pattern="[a-zA-Z0-9_]+"
                              title={t('auth.usernameHint', 'Solo letras, números y guiones bajos. No @ al inicio.')}
                            />
                          </div>
                          {validationErrors.username && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.username}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-semibold text-gray-700">
                            {t('auth.birthDateLabel', 'Fecha de nacimiento')}
                          </label>
                          <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                              type="date"
                              required
                              value={formData.birthDate}
                              onChange={(e) => {
                                setFormData({ ...formData, birthDate: e.target.value });
                                validateField('birthDate', e.target.value);
                              }}
                              className={`w-full pl-11 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${validationErrors.birthDate ? 'border-red-500' : 'border-gray-300'
                                }`}
                            />
                          </div>
                          {validationErrors.birthDate && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.birthDate}</p>
                          )}
                        </div>

                        <div className="space-y-3">
                          <label className="block text-sm font-semibold text-gray-700">
                            {t('auth.userTypeLabel', 'Tipo de cuenta')}
                          </label>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, userType: 'visitor' })}
                              className={`p-4 border-2 rounded-xl text-center transition-all transform hover:scale-105 ${formData.userType === 'visitor'
                                  ? 'border-purple-500 bg-purple-50 text-purple-700 shadow-lg'
                                  : 'border-gray-300 hover:border-gray-400 hover:shadow-md'
                                }`}
                            >
                              <Users className={`mx-auto mb-2 ${formData.userType === 'visitor' ? 'text-purple-600' : 'text-gray-500'}`} size={24} />
                              <div className="font-semibold">{t('auth.userType.visitor', 'Visitante')}</div>
                              <div className="text-xs text-gray-500 mt-1">{t('auth.userType.visitorHelp', 'Explorar y descubrir arte')}</div>
                            </button>
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, userType: 'artist' })}
                              className={`p-4 border-2 rounded-xl text-center transition-all transform hover:scale-105 ${formData.userType === 'artist'
                                  ? 'border-purple-500 bg-purple-50 text-purple-700 shadow-lg'
                                  : 'border-gray-300 hover:border-gray-400 hover:shadow-md'
                                }`}
                            >
                              <Palette className={`mx-auto mb-2 ${formData.userType === 'artist' ? 'text-purple-600' : 'text-gray-500'}`} size={24} />
                              <div className="font-semibold">{t('auth.userType.artist', 'Artista')}</div>
                              <div className="text-xs text-gray-500 mt-1">{t('auth.userType.artistHelp', 'Crear y vender arte')}</div>
                            </button>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl p-3">
                            <div className="mr-3">
                              <p className="text-sm font-semibold text-gray-700">
                                {t('auth.didit.title', 'Verificación de mayoría de edad')}
                              </p>
                              <p className="text-xs text-gray-500">
                                {diditStatus === 'approved'
                                  ? t(
                                      'auth.didit.approved',
                                      'Tu verificación de edad ha sido aprobada. Ya puedes crear tu cuenta.'
                                    )
                                  : t(
                                      'auth.didit.description',
                                      'Antes de registrarte, completa la verificación de tu edad con Didit usando tu documento de identidad.'
                                    )}
                              </p>
                            </div>
                            <span
                              className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                                diditStatus === 'approved'
                                  ? 'bg-green-100 text-green-700'
                                  : diditStatus === 'declined'
                                  ? 'bg-red-100 text-red-700'
                                  : diditStatus === 'in_review'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {diditStatus === 'approved' &&
                                t('auth.didit.status.approved', 'Aprobada')}
                              {diditStatus === 'declined' &&
                                t('auth.didit.status.declined', 'Rechazada')}
                              {diditStatus === 'in_review' &&
                                t('auth.didit.status.in_review', 'En revisión')}
                              {diditStatus === 'idle' &&
                                t('auth.didit.status.pending', 'Pendiente')}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={handleStartDiditVerification}
                            className="w-full px-4 py-2 border border-purple-500 text-purple-600 rounded-xl text-sm font-semibold hover:bg-purple-50 transition-colors"
                          >
                            {diditStatus === 'approved'
                              ? t('auth.didit.button.repeat', 'Repetir verificación con Didit')
                              : t('auth.didit.button.start', 'Iniciar verificación con Didit')}
                          </button>
                          {diditStatus === 'declined' && (
                            <p className="text-xs text-red-500">
                              {t(
                                'auth.didit.declinedHint',
                                'Tu verificación ha sido rechazada. Revisa que tu documento sea legible e inténtalo de nuevo.'
                              )}
                            </p>
                          )}
                        </div>

                        {formData.userType === 'artist' && (
                          <div className="space-y-3">
                            <label className="block text-sm font-semibold text-gray-700">
                              {t('auth.specialtiesLabel', 'Especialidades artísticas')}
                            </label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-xl p-3">
                              {Object.entries(categoryLabels).map(([key, label]) => (
                                <button
                                  key={key}
                                  type="button"
                                  onClick={() => toggleArtCategory(key as ArtCategory)}
                                  className={`p-3 text-xs border rounded-lg transition-all text-left touch-target ${formData.artCategories.includes(key as ArtCategory)
                                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                                    }`}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                            <p className="text-xs text-gray-500">{t('auth.specialtiesHelper', 'Selecciona las categorías que mejor representen tu arte')}</p>
                          </div>
                        )}
                      </>
                    )}

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        {t('auth.emailLabel', 'Dirección de email')}
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => {
                            setFormData({ ...formData, email: e.target.value });
                            validateField('email', e.target.value);
                          }}
                          className={`w-full pl-11 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${validationErrors.email ? 'border-red-500' : 'border-gray-300'
                            }`}
                          placeholder={t('auth.emailPlaceholder', 'tu@email.com')}
                        />
                      </div>
                      {validationErrors.email && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        {t('auth.passwordLabel', 'Contraseña')}
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          required
                          value={formData.password}
                          onChange={(e) => {
                            setFormData({ ...formData, password: e.target.value });
                            validateField('password', e.target.value);
                          }}
                          className={`w-full pl-11 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${validationErrors.password ? 'border-red-500' : 'border-gray-300'
                            }`}
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {validationErrors.password && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.password}</p>
                      )}
                      {!isLogin && formData.password && (
                        <div className="mt-2 space-y-1">
                          <div className={`text-xs flex items-center ${formData.password.length >= 6 ? 'text-green-600' : 'text-gray-400'}`}>
                            <div className={`w-1.5 h-1.5 rounded-full mr-2 ${formData.password.length >= 6 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                            {t('auth.passwordHint', 'Al menos 6 caracteres')}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={acceptTerms}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setAcceptTerms(checked);
                            if (checked) {
                              setValidationErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors.terms;
                                return newErrors;
                              });
                            }
                          }}
                          className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                        />
                        <span>
                          {t('auth.terms.accept', 'He leído y acepto los')}{' '}
                          <a
                            href={language === 'en' ? "https://soartistic.com/en/terminos-y-condiciones/" : "https://soartistic.com/terminos-y-condiciones/"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-600 hover:text-purple-700 underline"
                          >
                            {t('auth.terms.link', 'Términos y Condiciones')}
                          </a>
                        </span>
                      </label>
                      {validationErrors.terms && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.terms}</p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={
                        loading ||
                        submitting ||
                        !acceptTerms ||
                        (!isLogin && diditStatus !== 'approved')
                      }
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
                    >
                      {loading || submitting ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          {isLogin ? t('auth.loading.login', 'Iniciando sesión...') : t('auth.loading.register', 'Creando cuenta...')}
                        </div>
                      ) : (
                        isLogin ? t('auth.submit.login', 'Iniciar Sesión') : t('auth.submit.register', 'Crear Cuenta')
                      )}
                    </button>
                    {!isLogin && diditStatus !== 'approved' && (
                      <p className="mt-2 text-xs text-gray-500 text-center">
                        {t(
                          'auth.didit.required',
                          'Para crear tu cuenta debes completar antes la verificación de edad con Didit.'
                        )}
                      </p>
                    )}
                  </form>

                  <div className="mt-8 text-center">
                    <button
                      onClick={() => {
                        setIsLogin(!isLogin);
                        setError('');
                        setValidationErrors({});
                        setAcceptTerms(false);
                      }}
                      className="text-purple-600 hover:text-purple-700 font-semibold transition-colors duration-200"
                    >
                      {isLogin ? t('auth.toggle.toRegister', '¿No tienes cuenta? Crear una nueva') : t('auth.toggle.toLogin', '¿Ya tienes cuenta? Iniciar sesión')}
                    </button>
                  </div>

                  {isLogin && (
                    <div className="mt-6 text-center">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm text-gray-500 hover:text-gray-700 transition-colors duration-200"
                      >
                        {t('auth.forgotPasswordLink', '¿Olvidaste tu contraseña?')}
                      </button>
                    </div>
                  )}

                  {/* Trust indicators */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-center space-x-6 text-xs text-gray-500">
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                        <span>{t('auth.trust.secureData', 'Datos seguros')}</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                        <span>{t('auth.trust.ssl', 'SSL encriptado')}</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                        <span>{t('auth.trust.gdpr', 'GDPR compatible')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showDiditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800">
                {t('auth.didit.modalTitle', 'Verificación de identidad')}
              </h3>
              <button
                type="button"
                onClick={() => setShowDiditModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <CloseIcon size={18} />
              </button>
            </div>
            <div className="flex-1">
              <iframe
                src={diditVerificationUrl}
                className="w-full h-full border-0"
                title="Didit verification"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthForm;
